import { useMemo, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'

import { BASE_URL } from '../constants'

import useRecurrantFetch from '../hooks/useRecurrantFetch'

import {
  fetchSuccess,
  fetchStart,
  fetchFail,
  selectResults,
  clearSelection,
  hoverResultStart,
  hoverResultEnd,
} from './actions'

import {
  getResultById,
  getResults,
  getResultIds,
  getResultsIsLoading,
  getSelectedResultIds,
  getCurrentSelectedIndex,
} from './selectors'

export const useAddressSearch = () => {
  const dispatch = useDispatch()
  const [query, setQuery] = useState('')

  const search = useCallback(async () => {
    dispatch(fetchStart())
    try {
      const response = await fetch(`${BASE_URL}api/address/search`, {
        method: 'POST',
        headers: {
          ['Content-Type']: 'application/json',
        },
        body: JSON.stringify({
          query,
        }),
      })
      if (response.ok) {
        const results = await response.json()
        dispatch(fetchSuccess(results))
      } else {
        dispatch(fetchFail(response.error))
      }
    } catch (e) {
      dispatch(fetchFail(e))
    }
  }, [dispatch, query])

  return [query, { search, setQuery }]
}

export const useResult = (resultId) => {
  const dispatch = useDispatch()
  const result = useSelector(getResultById)(resultId)

  const select = useCallback(() => dispatch(selectResults([resultId])), [
    dispatch,
    resultId,
  ])

  return [result, { select }]
}

export const useResults = () => {
  const results = useSelector(getResults)
  return results
}

export const useResultsList = () => {
  const unsortedIds = useSelector(getResultIds)
  const byId = useSelector(getResultById)
  const isLoading = useSelector(getResultsIsLoading)

  const sortFunc = (a, b) => {
    return a.score > b.score ? -1 : 1
  }

  const resultIds = unsortedIds
    .map(byId)
    .sort(sortFunc)
    .map((e) => e.id)

  return [{ resultIds, isLoading }]
}

export const useResultsMap = () => {
  const dispatch = useDispatch()
  const selectedIds = useSelector(getSelectedResultIds)
  const current = useSelector(getCurrentSelectedIndex)
  const results = useSelector(getResults)

  const select = useCallback(
    (resultIds) => dispatch(selectResults(resultIds)),
    [dispatch]
  )
  const clear = useCallback(() => dispatch(clearSelection()), [dispatch])
  const selected = useMemo(() => ({ ids: selectedIds, current }), [
    selectedIds,
    current,
  ])
  return [
    { results, selected },
    { select, clear },
  ]
}
